/// Base da API. Em produção o Fastify serve o app e a API na mesma origem, e
/// isto fica vazio. Em desenvolvimento aponta para o backend na porta 3333 —
/// definido em .env.development, porque o celular acessa pelo IP da rede.
const BASE = import.meta.env.VITE_API_URL ?? "";

const ACCESS = "bipagem.access";
const REFRESH = "bipagem.refresh";

export type Role = "ADMIN" | "OPERATOR" | "SETOR2";
export type ScanResult = "OK" | "DUPLICATE" | "PENDING_ACCOUNT" | "UNIDENTIFIED";
export type Direction = "OUTBOUND" | "RETURN";

export type User = { id: string; name: string; email: string; role: Role };

export type AccountTally = {
  accountId: string | null;
  accountName: string;
  platformName: string | null;
  color: string | null;
  count: number;
};

export type Candidate = { id: string; name: string; color: string | null };

export type ScanOutcome = {
  result: ScanResult;
  sessionId: string;
  direction: Direction;
  package: {
    id: string;
    code: string;
    trackingCode: string | null;
    externalRef: string | null;
    labelFormat: string | null;
    status: string;
    scanCount: number;
    firstScanAt: string;
    priceCents: number | null;
  };
  account: { id: string; name: string; color: string | null } | null;
  platform: { id: string; name: string } | null;
  carrier: { id: string; name: string } | null;
  order: {
    id: string;
    externalId: string;
    items: Array<{ title: string; quantity: number; sku: string | null }>;
  } | null;
  previousScan: { at: string; by: string | null } | null;
  candidates: Candidate[];
  totals: AccountTally[];
  totalPackages: number;
};

export type SessionState = {
  sessionId: string;
  totals: AccountTally[];
  totalPackages: number;
  scans: Array<{
    id: string;
    rawCode: string;
    result: ScanResult;
    createdAt: string;
    package: { id: string; code: string; trackingCode: string | null; status: string };
    account: { id: string; name: string; color: string | null; platform: { name: string } } | null;
  }>;
};

/// A coleta aberta, vista por quem bipa.
///
/// Substitui o contador de SESSÃO, que somava desde sempre: a sessão abria no
/// primeiro bipe e só fechava se alguém apertasse um botão que ninguém
/// apertava. Esta janela é a mesma do painel — da última coleta fechada para
/// cá — e zera sozinha quando alguém finaliza a coleta.
/// Quanto UMA OUTRA pessoa bipou de uma conta.
export type PorPessoaEConta = AccountTally & {
  userId: string | null;
  userName: string;
};

export type ColetaDoOperador = {
  desde: string;
  /// O que ESTA pessoa bipou, por conta.
  meus: AccountTally[];
  meuTotal: number;
  /// O que TODO MUNDO MAIS bipou, pessoa por pessoa e conta por conta.
  outros: PorPessoaEConta[];
  /// O que a casa inteira bipou. É o número conferido com o motorista.
  total: number;
  porPlataforma: Array<{ plataforma: string; pacotes: number }>;
};

export type ColetaFechada = {
  id: string;
  fechadaEm: string;
  fechadaPor: string;
  pacotes: number;
  porPlataforma: Array<{ plataforma: string; pacotes: number }>;
  observacao: string | null;
  /// Quem veio buscar. `null` nas coletas anteriores ao campo.
  motorista: string | null;
  transportadora: string | null;
  /// Quais divisões este caminhão levou.
  divisoes: string[];
};

/// Um pacote que ainda não saiu, para escolher um a um na conferência.
export type PacoteDaColeta = {
  id: string;
  code: string;
  conta: string | null;
  divisao: string;
  transportadora: string | null;
  bipadoEm: string;
};

/// O que uma transportadora tem esperando.
export type ColetaDeTransportadora = {
  transportadora: string;
  carrierId: string;
  pacotes: number;
  porPlataforma: Array<{ plataforma: string; pacotes: number }>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

/// Lê um token guardado, descartando lixo.
///
/// Uma versão anterior gravava a string "undefined" no lugar do refresh token.
/// Quem usou o sistema naquele período tem esse lixo no navegador, e mandá-lo
/// ao servidor só rende 401. Tratar como ausente faz o app pedir login de novo,
/// que é o desfecho correto.
function lerToken(chave: string): string | null {
  const valor = localStorage.getItem(chave);
  if (!valor || valor === "undefined" || valor === "null") return null;
  return valor;
}

export const tokens = {
  get access() {
    return lerToken(ACCESS);
  },
  get refresh() {
    return lerToken(REFRESH);
  },
  save(access: string, refresh: string) {
    localStorage.setItem(ACCESS, access);
    localStorage.setItem(REFRESH, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS);
    localStorage.removeItem(REFRESH);
  },
};

/// Renova o access token usando o refresh. Guardado numa promessa única para
/// que uma rajada de bipagens que expire junto dispare UMA renovação, e não
/// uma por leitura — o refresh do backend é de uso único.
let refreshing: Promise<boolean> | null = null;

async function refreshAccess(): Promise<boolean> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) return false;

    const res = await fetch(`${BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      tokens.clear();
      return false;
    }

    const data = (await res.json()) as { accessToken: string; refreshToken?: string };

    // O backend devolve só o accessToken — ele NÃO rotaciona o refresh. Gravar
    // `data.refreshToken` direto salvava a string "undefined" por cima do
    // token bom, e a renovação seguinte deslogava o operador no meio da
    // expedição. Só sobrescreve quando vier um de verdade.
    tokens.save(data.accessToken, data.refreshToken ?? refreshToken);
    return true;
  })().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const headers = new Headers(init.headers);

  // Corpo e Content-Type andam juntos, e o Fastify é exigente nos dois
  // sentidos: anunciar JSON sem corpo dá 400 ("Body cannot be empty"), e mandar
  // POST sem Content-Type dá 415. Então requisição que aceita corpo e não tem
  // nada a enviar vai com um objeto vazio — é o caso de /scan-sessions/close.
  const aceitaCorpo = !["GET", "HEAD"].includes((init.method ?? "GET").toUpperCase());
  const body = init.body ?? (aceitaCorpo ? "{}" : undefined);

  if (body !== undefined) headers.set("Content-Type", "application/json");

  const access = tokens.access;
  if (access) headers.set("Authorization", `Bearer ${access}`);

  const res = await fetch(`${BASE}${path}`, { ...init, body, headers });

  if (res.status === 401 && retry && tokens.refresh) {
    if (await refreshAccess()) return request<T>(path, init, false);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(body?.message ?? `Erro ${res.status}`, res.status);
  }

  return (await res.json()) as T;
}

export type CheckOutcome = {
  alreadyScanned: boolean;
  previousScan: { at: string; by: string | null } | null;
  /// Por que não entra, quando o motivo não é uma leitura nossa — hoje, o
  /// Mercado Livre dizendo que o pacote já tinha saído.
  aviso: string | null;
  /// O que vai dentro do pacote, quando a plataforma sabe dizer.
  produtos: Array<{ title: string; quantity: number; sku: string | null }>;
  code: string;
  labelFormat: string | null;
  externalRef: string | null;
  trackingCode: string | null;
  status: string;
  account: { id: string; name: string; color: string | null } | null;
  platform: { id: string; name: string } | null;
  carrier: { id: string; name: string } | null;
  order: {
    id: string;
    externalId: string;
    items: Array<{ title: string; quantity: number; sku: string | null }>;
  } | null;
  candidates: Candidate[];
};

export type BatchItemPayload = {
  code: string;
  direction: Direction;
  accountId?: string;
  carrierId?: string;
  reason?: string;
  photoPath?: string;
  notes?: string;
  returnCostCents?: number;
};

export type BatchResult = {
  sessionId: string;
  registrados: Array<{ code: string; result: ScanResult; packageId: string }>;
  falhas: Array<{ code: string; message: string }>;
  totals: AccountTally[];
  totalPackages: number;
};

export type CarrierOption = { id: string; name: string };

/// O código que autoriza a coleta do dia de UMA conta do Mercado Livre.
/// `code` é null enquanto ninguém tiver cadastrado o de hoje daquela conta.
export type CodigoConta = {
  accountId: string;
  accountName: string;
  color: string | null;
  code: string | null;
  day: string;
  /// `MANUAL` (digitado) ou `MERCADO_LIVRE` (veio da integração).
  source: "MANUAL" | "MERCADO_LIVRE" | null;
  updatedAt: string | null;
  by: string | null;
};

export type CodigosDoDia = {
  codigos: CodigoConta[];
  /// `true` quando a busca automática está configurada no servidor. Enquanto
  /// for `false`, a tela não oferece um botão que só daria erro.
  automatico: boolean;
};

export type LeituraMl = {
  id: string;
  raw: string;
  format: "ML_HANDSHAKE" | "UNKNOWN";
  reference: string | null;
  logistic: string | null;
  codeShown: string | null;
  createdAt: string;
  by: string;
};

/// `repetido` marca a leitura que já tinha sido registrada hoje — o registro
/// devolvido é o original, não uma cópia nova.
export type LeituraRegistrada = {
  repetido: boolean;
  leitura: LeituraMl;
  codigos: CodigoConta[];
};

export const api = {
  async login(email: string, password: string): Promise<User> {
    const res = await fetch(`${BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new ApiError(body?.message ?? "E-mail ou senha incorretos.", res.status);
    }

    const data = (await res.json()) as {
      accessToken: string;
      refreshToken: string;
      user: User;
    };

    tokens.save(data.accessToken, data.refreshToken);
    return data.user;
  },

  me: () => request<User>("/auth/me"),

  session: () => request<SessionState>("/scan-sessions/current"),

  /// A coleta aberta: o que eu bipei e o que a casa bipou.
  minhaColeta: () => request<ColetaDoOperador>("/coletas/minha"),

  /// As coletas de hoje — a aberta e as que já foram fechadas.
  coletas: () =>
    request<{
      aberta: {
        desde: string;
        pacotes: number;
        porTransportadora: ColetaDeTransportadora[];
      };
      fechadas: ColetaFechada[];
    }>("/coletas"),

  /// Fecha a coleta: o motorista veio e levou o que estava aqui.
  ///
  /// Sem `divisoes`, leva tudo — como era antes de dar para escolher.
  fecharColeta: (fechamento: {
    carrierId: string;
    divisoes?: string[];
    motorista?: string;
    observacao?: string;
    transferir?: string[];
  }) =>
    request<{ coleta: ColetaFechada }>("/coletas/fechar", {
      method: "POST",
      body: JSON.stringify(fechamento),
    }),

  /// Acha um pacote que ainda não saiu, pelo código bipado. É a transferência
  /// entre caminhões — a Shopee que sobe no J3.
  pacoteDaColeta: (code: string) =>
    request<{ pacote: PacoteDaColeta }>("/coletas/pacote?code=" + encodeURIComponent(code)),

  /// Os pacotes de uma coleta já fechada — pra achar, dentro dela, o que
  /// precisa ser liberado.
  pacotesDaColeta: (id: string) => request<{ pacotes: PacoteDaColeta[] }>(`/coletas/${id}/pacotes`),

  /// Acha, dentro de uma coleta já fechada, o pacote de um código bipado —
  /// pra achar rápido no celular em vez de rolar a lista inteira.
  pacoteNaColeta: (coletaId: string, code: string) =>
    request<{ pacote: PacoteDaColeta }>(`/coletas/${coletaId}/pacote?code=${encodeURIComponent(code)}`),

  /// Tira um pacote de dentro de uma coleta já fechada — o motorista
  /// esqueceu na prateleira. O número da coleta original não muda; some só
  /// da conta e vira candidato à próxima passagem. Pedido do Abner em 22/09.
  liberarPacoteDeColeta: (packageId: string, bipadoPelaTransportadora: boolean, observacao?: string) =>
    request<{ ok: true }>(`/coletas/pacotes/${packageId}/liberar`, {
      method: "POST",
      body: JSON.stringify({ bipadoPelaTransportadora, observacao }),
    }),

  closeSession: () => request<{ closed: number }>("/scan-sessions/close", { method: "POST" }),

  /// Confere sem gravar. É o que roda a cada leitura enquanto o lote é montado.
  check: (code: string, direction: Direction = "OUTBOUND") =>
    request<CheckOutcome>("/scans/check", {
      method: "POST",
      body: JSON.stringify({ code, direction }),
    }),

  /// Grava o lote inteiro — é o botão Entregar.
  deliver: (items: BatchItemPayload[]) =>
    request<BatchResult>("/scans/batch", { method: "POST", body: JSON.stringify({ items }) }),

  carriers: () => request<CarrierOption[]>("/carriers"),

  /// Anexa a foto a uma devolução já registrada. Só depois do Entregar, porque
  /// antes disso o pacote ainda não existe no banco.
  uploadReturnPhoto: (packageId: string, dataUrl: string) =>
    request<{ caminho: string; bytes: number }>(`/returns/${packageId}/photo`, {
      method: "POST",
      body: JSON.stringify({ dataUrl }),
    }),

  /// Os códigos de autorização de hoje, um por conta do Mercado Livre.
  codigosDoDia: () => request<CodigosDoDia>("/authorization-codes"),

  /// Cadastra ou corrige o código de hoje de UMA conta.
  definirCodigoDoDia: (accountId: string, code: string) =>
    request<CodigosDoDia>(`/authorization-codes/${accountId}`, {
      method: "PUT",
      body: JSON.stringify({ code }),
    }),

  /// Registra que o setor 1 bipou uma etiqueta pronta para retirada.
  ///
  /// `silencioso: true` grava sem avisar ninguém — é o que a tela usa
  /// enquanto o lote está sendo montado. O aviso sai de uma vez só, ao final,
  /// por `avisarLote`.
  registrarRetirada: (code: string, silencioso = false) =>
    request<PickupRegistrado>("/pickups", {
      method: "POST",
      body: JSON.stringify({ code, silencioso }),
    }),

  /// Manda UM aviso para todo o lote bipado — o botão "Concluir".
  avisarLote: (ids: string[]) =>
    request<{ notificados: number; quantidade: number }>("/pickups/avisar-lote", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),

  retiradasDeHoje: () => request<{ rows: Pickup[] }>("/pickups?escopo=hoje&limit=30"),

  /// Escreve (ou apaga, com texto vazio) o recado de um pedido de retirada —
  /// o mesmo do painel. Fica só no nosso sistema: o Tiny não tem onde mostrar.
  /// Só o texto: o dia combinado com o cliente o painel cuida, e campo
  /// ausente não mexe no que já está lá.
  /// Qualquer pedido do Tiny pelo nome do cliente (Tiny ou Loja Integrada)
  /// ou pelo número — pra colocar observação.
  buscarPedidoTiny: (q: string) =>
    request<{ pedidos: PedidoBuscado[] }>(`/pickups/busca-tiny?q=${encodeURIComponent(q)}`),

  /// Os pedidos do Tiny que têm observação (últimos 30 dias).
  pedidosComObservacao: () => request<{ pedidos: PedidoBuscado[] }>("/pickups/com-observacao"),

  salvarObservacaoDeRetirada: (chave: string, texto: string, pelaBusca = false) =>
    request<{ observacao: ObservacaoDeRetirada | null }>("/pickups/observacao", {
      method: "PUT",
      body: JSON.stringify({ chave, texto, ...(pelaBusca ? { pelaBusca: true } : {}) }),
    }),

  /// O cliente veio de verdade e levou o pacote — etapa final. Desde 09/10
  /// vai com o pacote bipado de novo (`code`) e as fotos da entrega.
  confirmarClienteRetirou: (id: string, code: string, fotos: string[]) =>
    request<{ pickup: Pickup; jaRetirado: boolean }>(`/pickups/${id}/retirado`, {
      method: "POST",
      body: JSON.stringify({ code, fotos }),
    }),

  /// Pedidos de retirada que já apareceram no Tiny mas ninguém bipou ainda —
  /// `aguardando` é o que falta separar, `preparando` já saiu de aguardando
  /// e está sendo montado (informativo, ainda não é hora de bipar).
  pedidosTiny: () =>
    request<{ aguardando: PedidoTiny[]; preparando: PedidoTiny[] }>("/pickups/pedidos-tiny"),

  /// Cota o Uber pra entregar uma retirada — de graça, não desloca ninguém.
  /// Pra algumas retiradas específicas a própria casa decide entregar, em
  /// vez de esperar o cliente vir buscar.
  cotarUberRetirada: (code: string) =>
    request<CotacaoUberRetirada>("/pickups/uber/cotar", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),

  /// Confirma — AQUI desloca o motorista e cobra de verdade.
  confirmarUberRetirada: (
    code: string,
    quoteId: string,
    endereco: EnderecoRetirada,
    pesoGramas: number | null
  ) =>
    request<{ ok: true; link: string }>("/pickups/uber/confirmar", {
      method: "POST",
      body: JSON.stringify({ code, quoteId, endereco, pesoGramas }),
    }),

  /// Registra a leitura do QR do motorista.
  registrarLeitura: (raw: string) =>
    request<LeituraRegistrada>("/handshakes", {
      method: "POST",
      body: JSON.stringify({ raw }),
    }),

  /// Busca o código de hoje de UMA conta na API do Mercado Livre.
  atualizarCodigoPeloMl: (accountId: string) =>
    request<CodigosDoDia>(`/authorization-codes/${accountId}/sync`, { method: "POST" }),

  leiturasDeHoje: () =>
    request<{ rows: LeituraMl[] }>("/handshakes?escopo=hoje&limit=20"),

  assignAccount: (packageId: string, accountId: string) =>
    request<{ ok: true }>(`/packages/${packageId}/account`, {
      method: "PATCH",
      body: JSON.stringify({ accountId }),
    }),
};

/// Um pacote pronto na bancada, esperando o setor 2 vir buscar.
export type Pickup = {
  id: string;
  code: string;
  /// Achado automaticamente na cópia local do Tiny. `null` quando o Tiny não
  /// está conectado ou o pedido ainda não foi sincronizado.
  clienteNome: string | null;
  createdAt: string;
  scannedBy: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  pickedUpAt: string | null;
  pickedUpBy: string | null;
  /// Quantas fotos tiradas na entrega ao cliente.
  fotos: number;
  /// A chave do recado (o número do pedido no Tiny, ou o código bipado).
  chave: string;
  observacao: ObservacaoDeRetirada | null;
};

/// Um pedido do Tiny achado pela busca.
export type PedidoBuscado = {
  numero: number;
  clienteNome: string;
  situacao: string;
  formaEnvio: string | null;
  canal: string | null;
  criadoEm: string;
  chave: string;
  observacao: ObservacaoDeRetirada | null;
};

/// O recado de um pedido de retirada — só no nosso sistema.
export type ObservacaoDeRetirada = {
  texto: string;
  retiraEm: string | null;
  retiraHora: string | null;
  autor: string | null;
  atualizadoEm: string;
};

export type PickupRegistrado = {
  pickup: Pickup;
  /// `true` quando o mesmo código já estava aberto — o operador bipou de
  /// novo o mesmo pacote, e nada foi duplicado.
  jaAberta: boolean;
  notificados: number;
};

/// Um pedido de retirada que o Tiny já mostra, mas ninguém bipou ainda.
export type PedidoTiny = {
  numero: number;
  clienteNome: string;
  atualizadoEm: string;
  chave: string;
  observacao: ObservacaoDeRetirada | null;
};

/// O endereço achado no Tiny (ou na Loja Integrada) para chamar o Uber.
export type EnderecoRetirada = {
  endereco: string;
  numero: string;
  complemento?: string;
  bairro: string;
  municipio: string;
  cep: string;
  uf: string;
  nomeContato: string;
  telefoneContato?: string;
};

/// Cotação grátis: mostra endereço e preço antes de gastar de verdade.
export type CotacaoUberRetirada = {
  quoteId: string;
  feeCents: number;
  etaMinutos: number | null;
  endereco: EnderecoRetirada;
  /// `null` quando não deu pra calcular o peso — a Uber decide o veículo só
  /// pela disponibilidade, sem essa informação.
  pesoGramas: number | null;
};
